import React, { useState, useEffect } from "react";
import { Modal } from "@mantine/core";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { getApiEndpoint } from "../../../config/config";
import "./TeamEventsModal.css";
import { handleGenericError } from "../../../utils/errorHandler";

function TeamEventsModal({ isOpen, onClose }) {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess } = useToast();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchMyTeamEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const getEventEndTime = event => {
    const scheduledTime = new Date(event.scheduled_time);
    const durationMinutes = Number(event.duration_minutes || 60);
    return new Date(scheduledTime.getTime() + durationMinutes * 60000);
  };

  const fetchMyTeamEvents = async () => {
    try {
      setLoading(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(
        getApiEndpoint("/v1/attendance/my-team-events"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        const eventsData = result.data?.events || [];

        // Keep events in Upcoming until their scheduled duration has ended
        const now = new Date();
        const upcomingEvents = eventsData.filter(
          event => getEventEndTime(event) >= now
        );

        setEvents(upcomingEvents);
      } else {
        setEvents([]);
      }
    } catch (error) {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAttended = async (meetingId, event) => {
    event.stopPropagation();
    try {
      const jwt = await getVerifiedJWT();

      const response = await fetch(
        getApiEndpoint(`/v1/attendance/team-events/${meetingId}/attend`),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to mark attendance");
      }

      showSuccess("Attendance marked successfully");

      // Update local state
      setEvents(prevEvents =>
        prevEvents.map(e =>
          e.meeting_id === meetingId ? { ...e, status: "completed" } : e
        )
      );
    } catch (error) {
      handleGenericError(error, "Failed to mark attendance");
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title="Team Events"
      size="lg"
      centered
    >
      <div className="team-events-modal-content">
        {loading ? (
          <div className="loading-state">
            <span className="material-icons rotating-icon">refresh</span>
            <p>Loading events...</p>
          </div>
        ) : events.length === 0 ? (
          <div className="empty-state">
            <span className="material-icons">event_available</span>
            <p>No upcoming team events</p>
          </div>
        ) : (
          <div className="events-list">
            {events.map(event => {
              const eventDate = new Date(event.scheduled_time);
              const now = new Date();
              const isToday = eventDate.toDateString() === now.toDateString();
              const isTomorrow =
                eventDate.toDateString() ===
                new Date(Date.now() + 86400000).toDateString();
              const isPast = getEventEndTime(event) < now;
              const isCompleted = event.status === "completed";

              return (
                <div
                  key={event.meeting_id}
                  className={`event-item ${isPast ? "past" : isToday ? "today" : "upcoming"} ${isCompleted ? "completed" : ""}`}
                >
                  <div className="event-date-box">
                    <div className="event-month">
                      {eventDate.toLocaleDateString("en-US", {
                        month: "short",
                      })}
                    </div>
                    <div className="event-day">{eventDate.getDate()}</div>
                  </div>
                  <div className="event-details">
                    <div className="event-title">
                      {event.event_title || "Team Event"}
                    </div>
                    <div className="event-time-info">
                      {eventDate.toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      })}
                      {isPast && !isCompleted && (
                        <span className="event-badge badge-past">Past</span>
                      )}
                      {isCompleted && (
                        <span className="event-badge badge-completed">
                          Attended
                        </span>
                      )}
                      {isToday && !isPast && !isCompleted && (
                        <span className="event-badge badge-today">Today</span>
                      )}
                      {isTomorrow && !isCompleted && (
                        <span className="event-badge badge-tomorrow">
                          Tomorrow
                        </span>
                      )}
                    </div>
                    {event.location && (
                      <div className="event-location">
                        <span className="material-icons location-icon">
                          place
                        </span>
                        {event.location}
                      </div>
                    )}
                  </div>
                  {isPast && !isCompleted ? (
                    <button
                      className="mark-attended-btn"
                      onClick={e => handleMarkAttended(event.meeting_id, e)}
                    >
                      <span className="material-icons">check_circle</span>
                      <span>Mark Attended</span>
                    </button>
                  ) : (
                    <span className="material-icons event-chevron">
                      chevron_right
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default TeamEventsModal;
